import { types, Instance, cast, getSnapshot } from 'mobx-state-tree';

import { getGoalReached, getOverAllPl, runInAction } from '@trader/utils';

import {
  getPortfolioDemoAsync,
  getProfileDemoAsync,
  makeDepositAsync,
  getTradingAccountsAsync,
  changeTradingAccountAsync,
  getUserBalanceAsync,
  getSettingsAsync,
  getPspsAsync,
  getTransactionsAsync,
  getTransfersHistoryAsync,
  updateSettingsAsync,
  getChallengesAsync,
  getPurchaseHistoryAsync,
} from './actions';

const tradingAccountModel = types.model('tradingAccount', {
  platformLogin: types.string,
  accountType: types.string,
  product: types.optional(types.string, ''),
  currency: types.string,
  currencySymbol: types.string,
  tradingId: types.number,
  isInUse: types.boolean,
});

// overallProfit / amount * 100

const challengeModel = types.model('challengeModel', {
  id: types.number,
  status: types.string,
  accountId: types.number,
  purchaseAmount: types.number,
  challengeAmount: types.number,
  name: types.string,
  payout: types.number,
  balance: types.number,
  profitTarget: types.number,
  profitLoss24: types.number,
  currencySymbol: types.string,
  profitLossOverall: types.number,
  tradingDays: types.number,
  minTradingDays: types.number,
  remainingTime: types.number,
  goalReached: types.number,
  result: types.string,
});

const initialState = {
  portfolio: {
    currency: '',
    currencySymbol: '',
    equity: 0,
    balance: 0,
    startDayPl: 0,
    dailyPl: 0,
    pl: 0,
    freeMargin: 0,
    usedMargin: 0,
    availableForWithdrawal: 0,
    marginLevel: 0,
    maintenanceMargin: 0,
    credit: 0,
  },
  profile: {
    customerId: 0,
    mt5Id: 0,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    country: '',
    isRegistrationCompleted: false,
    isAcceptedAccountApproved: false,
    isTradingAllowed: false,
    isDepositAllowed: false,
    complianceStage: '',
    scoreLevel: '',
  },
  tradingAccounts: [],
  challenges: [],
  settings: {
    isTradingCentral: false,
    isCrmTradingCentral: false,
    isDisplayedByUser: false,
    isEdgeX: false,
    isCrmEdgeX: false,
    isEdgeXDisplayedByUser: false,
    isEdgeZone: false,
    isCrmEdgeZone: false,
    isEdgeZoneDisplayedByUser: false,
  },
};

export const userModel = types
  .model('userModel', {
    portfolio: types.model({
      currency: types.optional(types.string, ''),
      currencySymbol: types.optional(types.string, ''),
      equity: types.number,
      balance: types.number,
      startDayPl: types.number,
      dailyPl: types.number,
      pl: types.number,
      freeMargin: types.number,
      usedMargin: types.number,
      availableForWithdrawal: types.number,
      marginLevel: types.number,
      maintenanceMargin: types.number,
      credit: types.optional(types.number, 0),
    }),
    profile: types.model({
      customerId: types.number,
      mt5Id: types.maybeNull(types.number),
      firstName: types.maybeNull(types.string),
      lastName: types.maybeNull(types.string),
      email: types.string,
      phone: types.maybeNull(types.string),
      address: types.maybeNull(types.string),
      country: types.string,
      isRegistrationCompleted: types.boolean,
      isAcceptedAccountApproved: types.boolean,
      isTradingAllowed: types.boolean,
      isDepositAllowed: false,
      complianceStage: types.string,
      scoreLevel: types.string,
    }),
    tradingAccounts: types.array(tradingAccountModel),
    challenges: types.array(challengeModel),
    settings: types.optional(
      types.model('settings', {
        isTradingCentral: types.boolean,
        isCrmTradingCentral: types.boolean,
        isDisplayedByUser: types.boolean,
        isEdgeX: types.boolean,
        isCrmEdgeX: types.boolean,
        isEdgeXDisplayedByUser: types.boolean,
        isEdgeZone: types.boolean,
        isCrmEdgeZone: types.boolean,
        isEdgeZoneDisplayedByUser: types.boolean,
      }),
      {
        isTradingCentral: false,
        isCrmTradingCentral: false,
        isDisplayedByUser: false,
        isEdgeX: false,
        isCrmEdgeX: false,
        isEdgeXDisplayedByUser: false,
        isEdgeZone: false,
        isCrmEdgeZone: false,
        isEdgeZoneDisplayedByUser: false,
      }
    ),
    getPortfolioDemoAsync,
    getProfileDemoAsync,
    makeDepositAsync,
    getPspsAsync,
    getTransactionsAsync,
    getSettingsAsync,
    updateSettingsAsync,
    getTradingAccountsAsync,
    changeTradingAccountAsync,
    getUserBalanceAsync,
    getTransfersHistoryAsync,
    getPurchaseHistoryAsync,
    getChallengesAsync,
  })
  .views(state => ({
    getActiveAccountChallenge: () => {
      const accountInUse = state.tradingAccounts.find(
        (account: TTradingAccountModel) => account.isInUse
      );

      if (accountInUse) {
        return state.challenges.find(
          c => c.accountId === accountInUse.tradingId
        );
      }
      return null;
    },
    getAccountChallenge: (accountId: number) => {
      return state.challenges.find(c => c.accountId === accountId);
    },
    isDemoAccInUse: () =>
      state.tradingAccounts.find((acc: TTradingAccountModel) => acc.isInUse)
        ?.accountType === 'Demo',
    activeTradingAcc: (): TTradingAccountModel => {
      const acc = state.tradingAccounts.find(
        (account: TTradingAccountModel) => account.isInUse
      );
      if (acc) {
        return getSnapshot(acc) as TTradingAccountModel;
      }
      return {
        platformLogin: '',
        accountType: '',
        product: '',
        currency: '',
        currencySymbol: '',
        tradingId: 0,
        isInUse: false,
      };
    },
  }))
  .actions(state => ({
    runInAction,
    reset: () => {
      state.portfolio = initialState.portfolio;
      state.profile = initialState.profile;
      state.tradingAccounts = cast(initialState.tradingAccounts);
      state.challenges = cast(initialState.challenges);
      state.settings = initialState.settings;
      state.getPortfolioDemoAsync.hasEverBeenRun = false;
    },
    updatePortfolio: payload => {
      state.portfolio = {
        ...state.portfolio,
        ...payload,
      };
    },
    updatePortfolioFromSockets: (payload, platformLogin: string) => {
      const activeTradingAccount = state.tradingAccounts.find(
        (account: TTradingAccountModel) => account.isInUse
      );
      if (
        Number(activeTradingAccount?.platformLogin) === Number(platformLogin)
      ) {
        state.portfolio = {
          ...state.portfolio,
          ...payload,
        };
      }
    },
    updateChallengeFromSockets: payload => {
      const activeTradingAccount = state.tradingAccounts.find(
        (account: TTradingAccountModel) => account.isInUse
      );

      const activeChallenge = state.getActiveAccountChallenge();

      if (activeChallenge?.status === 'DISABLED') {
        return;
      }

      const updatedChallenges = state.challenges.map(c => {
        if (c.accountId === activeTradingAccount?.tradingId) {
          c.profitLossOverall = getOverAllPl(
            payload.balance,
            c.challengeAmount,
            payload.profit
          );
          c.goalReached = getGoalReached(
            c.profitTarget,
            getOverAllPl(payload.balance, c.challengeAmount, payload.profit),
            c.challengeAmount
          );
        }
        return c;
      });

      state.challenges = cast(updatedChallenges);
    },
  }));

export const user = types.optional(userModel, initialState);

export type TTradingAccountModel = Instance<typeof tradingAccountModel>;
export type TChallengeModel = Instance<typeof challengeModel>;
export type TUserStore = Instance<typeof userModel>;
